import ReferencePathService from "~/services/referencePathServices/referencePathService";
import {
  IReferencePathNodeFormElement,
  IReferencePathNodeSpecific,
  IReferencePathReferenceDetails,
  IReferencePathReferenceDetailsDateTimePicker,
  IReferencePathReferenceDetailsPickList,
  ReferencePathReferenceDetailsType
} from "~/interfaces/referencePathInterfaces"
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import {GenericType} from '~/interfaces/genericTypeInterfaces'
import {ModelType} from '~/enums/modelType'
import {IOrganization} from '~/interfaces/organizationInterfaces'
import {NumberType} from '~/enums/numberType'
import {PermitteeType} from "~/enums/permitteeType";
import {InputFormatType} from "../../../formability/form_builder/models/inputFormatType";

export default class FormElementPathService extends ReferencePathService {
  imposterModelType: ModelType = ModelType.FormElement

  constructor(private formElementNode: IReferencePathNodeFormElement) {
    super();
  }

  get label(): string {
    return this.formElementNode.reference()?.referenceNameOrDefault;
  }

  /**
   * Crumb from top-level element down to but not including for the element itself
   */
  get breadcrumbs(): string[] {
    const element = this.reference()
    if (!element) return []
    return this.reference().ancestorElementBreadcrumbs
  }

  get nodes(): IReferencePathNodeFormElement[] {
    return [this.formElementNode];
  }

  reference(): FormElement {
    return FormElement.repo.findById(this.formElementNode.reference_id);
  }

  get referenceGenericModelType(): GenericType {
    return this.reference()?.genericType
  }

  get referenceModelType(): ModelType {
    return this.reference()?.modelType
  }

  canLinkTo(node: IReferencePathNodeSpecific): boolean {
    let decision = !node // nothing can be linked to it - end of the line
    this.debugCanLinkTo('FormElementPathService', this.formElementNode, node, decision)
    return decision
  }

  get referenceIsCollection(): boolean {
    return this.reference()?.isDataCollection
  }

  get referenceIsAppendable(): boolean {
    // check for never appendables
    if (this.referenceIsPickList() || this.referenceIsDateTime() || this.referenceIsSignature()) return false;
    // check for appendables
    return this.reference()?.isDataCollection || this.referenceIsLookupField() || (this.referenceIsText() && !this.referenceIsCurrency())
  }

  get referenceIsSelectable(): boolean {
    return this.referenceIsCheckboxGroup();
  }

  get referenceDetails(): IReferencePathReferenceDetails {
    const ref = this.reference()
    if (this.referenceGenericModelType === ModelType.DateTime) {
      return {
        type: ReferencePathReferenceDetailsType.DateTimePicker,
        mode: ref.datetime_mode,
        min: ref.min_datetime_formula ? null : ref.min_datetime,
        max: ref.max_datetime_formula ? null : ref.max_datetime,
      }
    } else if (this.referenceIsPickList()) {
      return {
        type: ReferencePathReferenceDetailsType.PickList,
        selectionType: this.reference().selectionType,
        options: ref.formElementsArray.map(e => {
          return { label: e.defaultLabelTitle, value: e.client_id }
        }),
      }
    } else if (this.referenceIsNumber()) {
      return {
        type: ReferencePathReferenceDetailsType.Number,
        numType: NumberType.float,
        min: null,
        max: null,
      }
    } else {
      return {
        type: ReferencePathReferenceDetailsType.Text,
      }
    }
  }

  toUniversalValue(value: any, ...args: any[]) {
    return this.reference()?.toUniversalValue(value)
  }

  fromUniversalValue(value: any) {
    return this.reference()?.fromUniversalValue(value)
  }

  get organization(): IOrganization {
    return this.reference()?.organization
  }

  get referencePermitteeType(): PermitteeType {
    if (this.reference()?.format_type === InputFormatType.Email) {
      return PermitteeType.email;
    }
  }

  referenceIsPickList(): this is { referenceDetails: IReferencePathReferenceDetailsPickList } {
    return this.reference()?.isPickList;
  }

  referenceIsDateTime(): this is { referenceDetails: IReferencePathReferenceDetailsDateTimePicker } {
    return this.reference()?.isDateTime;
  }

  referenceIsSignature(): boolean {
    return this.reference()?.isSignature;
  }

  referenceIsLookupField(): boolean {
    return this.reference()?.isLookupField;
  }

  referenceIsCheckboxGroup(): boolean {
    return this.reference()?.isCheckboxGroup;
  }

  referenceIsCurrency(): boolean {
    return this.reference()?.isCurrencyFormat;
  }

}